/**
 * UAT hotfix (2026-07-25) · Work Role master admin surface.
 * Separate from HR Designation. Includes eligibility mapping editor.
 */
import { useEffect, useState } from "react";
import { api, formatApiError } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../../components/ui/select";
import {
    Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../../components/ui/table";
import {
    Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "../../components/ui/dialog";
import { Badge } from "../../components/ui/badge";
import { toast } from "sonner";


const CATEGORIES = [
    "engineering", "delivery", "consulting", "finance", "sales",
    "operations", "support", "management", "other",
];


export default function WorkRoleCataloguePage() {
    const [items, setItems] = useState([]);
    const [designations, setDesignations] = useState([]);
    const [includeInactive, setIncludeInactive] = useState(false);
    const [loading, setLoading] = useState(false);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [form, setForm] = useState({ code: "", name: "", description: "", category: "other" });
    const [eligOpen, setEligOpen] = useState(false);
    const [eligTarget, setEligTarget] = useState(null);
    const [eligMap, setEligMap] = useState({ eligible_designations: [], eligible_employee_ids: [] });
    const [eligEmpQuery, setEligEmpQuery] = useState("");
    const [eligEmpResults, setEligEmpResults] = useState([]);

    useEffect(() => {
        api.get("/hr/designation/list").then((r) => {
            setDesignations(r.data?.items || r.data || []);
        }).catch(() => {});
    }, []);

    const load = () => {
        setLoading(true);
        api.get(`/projects/work-role/list?include_inactive=${includeInactive}&page_size=500`)
            .then((r) => setItems(r.data?.items || []))
            .catch((e) => toast.error(formatApiError(e)))
            .finally(() => setLoading(false));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(load, [includeInactive]);

    const startCreate = () => {
        setEditing(null);
        setForm({ code: "", name: "", description: "", category: "other" });
        setDialogOpen(true);
    };
    const startEdit = (wr) => {
        setEditing(wr);
        setForm({ code: wr.code, name: wr.name, description: wr.description || "", category: wr.category });
        setDialogOpen(true);
    };
    const save = async () => {
        try {
            if (editing) {
                await api.patch(`/projects/work-role/${editing.id}`, {
                    name: form.name, description: form.description, category: form.category,
                });
                toast.success("Work Role updated.");
            } else {
                await api.post("/projects/work-role", form);
                toast.success("Work Role created.");
            }
            setDialogOpen(false);
            load();
        } catch (e) { toast.error(formatApiError(e)); }
    };
    const toggleActive = async (wr) => {
        try {
            await api.patch(`/projects/work-role/${wr.id}`, { is_active: !wr.is_active });
            toast.success(`Work Role ${wr.is_active ? "deactivated" : "reactivated"}.`);
            load();
        } catch (e) { toast.error(formatApiError(e)); }
    };

    const openEligibility = async (wr) => {
        setEligTarget(wr);
        try {
            const r = await api.get(`/projects/work-role/${wr.id}/eligibility`);
            setEligMap({
                eligible_designations: r.data?.eligible_designations || [],
                eligible_employee_ids: r.data?.eligible_employee_ids || [],
            });
            setEligEmpQuery(""); setEligEmpResults([]);
            setEligOpen(true);
        } catch (e) { toast.error(formatApiError(e)); }
    };
    const saveEligibility = async () => {
        try {
            await api.put(`/projects/work-role/${eligTarget.id}/eligibility`, eligMap);
            toast.success("Eligibility mapping saved.");
            setEligOpen(false);
        } catch (e) { toast.error(formatApiError(e)); }
    };
    const searchEmp = async (q) => {
        setEligEmpQuery(q);
        if (!q || q.length < 2) { setEligEmpResults([]); return; }
        try {
            const r = await api.get(`/hr/employee/list?q=${encodeURIComponent(q)}&page_size=15`);
            setEligEmpResults(r.data?.items || r.data || []);
        } catch { setEligEmpResults([]); }
    };
    const toggleDes = (id) => {
        setEligMap((m) => ({
            ...m,
            eligible_designations: m.eligible_designations.includes(id)
                ? m.eligible_designations.filter((x) => x !== id)
                : [...m.eligible_designations, id],
        }));
    };
    const addEmp = (empId) => {
        if (eligMap.eligible_employee_ids.includes(empId)) return;
        setEligMap((m) => ({ ...m, eligible_employee_ids: [...m.eligible_employee_ids, empId] }));
    };
    const removeEmp = (empId) => {
        setEligMap((m) => ({ ...m, eligible_employee_ids: m.eligible_employee_ids.filter((x) => x !== empId) }));
    };

    return (
        <div className="p-6 space-y-4" data-testid="work-role-master-page">
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-xl font-semibold">Work Role Master</h1>
                    <p className="text-sm text-slate-500">
                        Project-scoped Work Role catalogue. Distinct from HR Designation.
                    </p>
                </div>
                <div className="flex items-center gap-2">
                    <label className="flex items-center gap-1 text-xs text-slate-500">
                        <input type="checkbox" checked={includeInactive}
                            onChange={(e) => setIncludeInactive(e.target.checked)} />
                        Include inactive
                    </label>
                    <Button onClick={startCreate} data-testid="work-role-create-btn">
                        + New Work Role
                    </Button>
                </div>
            </div>
            <div className="border rounded-md">
                <Table>
                    <TableHeader>
                        <TableRow>
                            <TableHead>Code</TableHead>
                            <TableHead>Name</TableHead>
                            <TableHead>Category</TableHead>
                            <TableHead>Status</TableHead>
                            <TableHead className="text-right">Actions</TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {loading && (
                            <TableRow><TableCell colSpan={5} className="text-center text-slate-400">Loading...</TableCell></TableRow>
                        )}
                        {!loading && items.length === 0 && (
                            <TableRow>
                                <TableCell colSpan={5} className="text-center text-slate-400 py-6">
                                    No Work Roles. Configure the Work Role Master and eligibility to unblock work-item assignment.
                                </TableCell>
                            </TableRow>
                        )}
                        {items.map((wr) => (
                            <TableRow key={wr.id}>
                                <TableCell className="font-mono text-xs">{wr.code}</TableCell>
                                <TableCell>{wr.name}</TableCell>
                                <TableCell className="capitalize">{wr.category}</TableCell>
                                <TableCell>
                                    <Badge variant={wr.is_active ? "default" : "secondary"}>
                                        {wr.is_active ? "Active" : "Inactive"}
                                    </Badge>
                                </TableCell>
                                <TableCell className="text-right space-x-2">
                                    <Button size="sm" variant="outline" onClick={() => openEligibility(wr)}
                                        data-testid={`work-role-eligibility-${wr.id}`}>
                                        Manage Eligibility
                                    </Button>
                                    <Button size="sm" variant="outline" onClick={() => startEdit(wr)}>Edit</Button>
                                    <Button size="sm" variant="outline" onClick={() => toggleActive(wr)}>
                                        {wr.is_active ? "Deactivate" : "Reactivate"}
                                    </Button>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </div>

            {/* Work Role edit dialog */}
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>{editing ? "Edit Work Role" : "New Work Role"}</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-3 py-2">
                        <div>
                            <Label>Code <span className="text-rose-600">*</span></Label>
                            <Input value={form.code}
                                onChange={(e) => setForm({ ...form, code: e.target.value.toLowerCase() })}
                                disabled={!!editing}
                                placeholder="e.g. backend_dev" />
                        </div>
                        <div>
                            <Label>Name <span className="text-rose-600">*</span></Label>
                            <Input value={form.name}
                                onChange={(e) => setForm({ ...form, name: e.target.value })} />
                        </div>
                        <div>
                            <Label>Category</Label>
                            <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                                <SelectTrigger><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    {CATEGORIES.map((c) => (
                                        <SelectItem key={c} value={c} className="capitalize">{c}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        <div>
                            <Label>Description</Label>
                            <Textarea value={form.description} rows={2}
                                onChange={(e) => setForm({ ...form, description: e.target.value })} />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
                        <Button onClick={save} disabled={!form.code || !form.name}>
                            {editing ? "Save changes" : "Create"}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Eligibility mapping dialog */}
            <Dialog open={eligOpen} onOpenChange={setEligOpen}>
                <DialogContent className="max-w-2xl" data-testid="work-role-eligibility-dialog">
                    <DialogHeader>
                        <DialogTitle>Manage Eligibility · {eligTarget?.name}</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 py-2">
                        <div>
                            <Label>Eligible Designations (bulk mapping)</Label>
                            <div className="border rounded-md p-2 max-h-40 overflow-y-auto text-xs">
                                {designations.map((d) => (
                                    <label key={d.id} className="flex items-center gap-2 py-0.5">
                                        <input type="checkbox"
                                            checked={eligMap.eligible_designations.includes(d.id)}
                                            onChange={() => toggleDes(d.id)}
                                            data-testid={`elig-des-${d.id}`} />
                                        <span>{d.name}</span>
                                    </label>
                                ))}
                                {designations.length === 0 && (
                                    <div className="text-slate-400">No designations loaded.</div>
                                )}
                            </div>
                        </div>
                        <div>
                            <Label>Individual Employee Overrides</Label>
                            <Input
                                placeholder="Search employees by name / code / email…"
                                value={eligEmpQuery}
                                onChange={(e) => searchEmp(e.target.value)}
                                data-testid="elig-emp-search"
                            />
                            {eligEmpResults.length > 0 && (
                                <div className="border rounded-md mt-1 max-h-32 overflow-y-auto text-xs">
                                    {eligEmpResults.map((e) => (
                                        <div key={e.id}
                                            className="flex items-center justify-between px-2 py-1 hover:bg-slate-50 cursor-pointer"
                                            onClick={() => { addEmp(e.id); setEligEmpQuery(""); setEligEmpResults([]); }}>
                                            <span>{e.full_name} <span className="text-slate-400">· {e.employee_code} · {e.designation}</span></span>
                                            <span className="text-slate-400">+</span>
                                        </div>
                                    ))}
                                </div>
                            )}
                            <div className="flex flex-wrap gap-1 mt-2 text-xs">
                                {eligMap.eligible_employee_ids.map((id) => (
                                    <Badge key={id} variant="secondary" className="pl-2 pr-1">
                                        {id}
                                        <button className="ml-1 text-rose-600" onClick={() => removeEmp(id)}>×</button>
                                    </Badge>
                                ))}
                            </div>
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setEligOpen(false)}>Cancel</Button>
                        <Button onClick={saveEligibility} data-testid="elig-save">Save Eligibility</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
